import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';

export type BadgeVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'light'
  | 'dark'
  | 'transparent';

export type BadgeTextColor = 'white' | 'dark';

/*
 * Metrics and palettes mirror Dashkit's .badge and Bootstrap .bg-* rules in
 * src/vendor/dashkit/css/theme.bundle.css. Bootstrap's `bg` and `text` props
 * migrate to `variant` and `textColor`; `pill` keeps its existing meaning.
 */
const badgeVariants = cva(
  'tw:inline-block tw:whitespace-nowrap tw:rounded-md tw:px-[0.5em] tw:py-[0.33em] tw:text-center tw:align-middle tw:text-[76%] tw:font-normal tw:leading-none tw:text-white tw:empty:hidden',
  {
    variants: {
      variant: {
        primary: 'tw:bg-primary',
        secondary: 'tw:bg-secondary',
        success: 'tw:bg-success',
        danger: 'tw:bg-destructive',
        warning: 'tw:bg-warning',
        info: 'tw:bg-info',
        light: 'tw:bg-light tw:text-foreground',
        dark: 'tw:bg-dark',
        transparent: 'tw:bg-transparent',
      },
      textColor: {
        white: 'tw:text-white',
        dark: 'tw:text-foreground',
      },
      pill: {
        true: 'tw:rounded-[50rem] tw:px-[0.6em]',
        false: null,
      },
      soft: {
        true: null,
        false: null,
      },
    },
    /*
     * Background hex values mirror Dashkit's .bg-*-soft rules. Foreground
     * tokens mirror the matching .badge.bg-*-soft rules.
     */
    compoundVariants: [
      {
        variant: 'primary',
        soft: true,
        className: 'tw:bg-[#d1e1e4] tw:text-primary',
      },
      {
        variant: 'secondary',
        soft: true,
        className: 'tw:bg-[#e8e8e5] tw:text-secondary',
      },
      {
        variant: 'success',
        soft: true,
        className: 'tw:bg-[#cce5e3] tw:text-success',
      },
      {
        variant: 'danger',
        soft: true,
        className: 'tw:bg-[#f3cfcf] tw:text-destructive',
      },
      {
        variant: 'warning',
        soft: true,
        className: 'tw:bg-[#ffead3] tw:text-warning',
      },
      {
        variant: 'info',
        soft: true,
        className: 'tw:bg-[#d7eff6] tw:text-info',
      },
      {
        variant: 'light',
        soft: true,
        className: 'tw:bg-[#fcfcfa] tw:text-light',
      },
      {
        variant: 'dark',
        soft: true,
        className: 'tw:bg-[#dcdddf] tw:text-dark',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      pill: false,
      soft: false,
    },
  },
);

export type BadgeProps = React.HTMLAttributes<HTMLSpanElement> & {
  variant?: BadgeVariant;
  textColor?: BadgeTextColor;
  pill?: boolean;
  soft?: boolean;
};

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, textColor, pill, soft, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(
        badgeVariants({ variant, textColor, pill, soft }),
        className,
      )}
      {...props}
    />
  ),
);
Badge.displayName = 'Badge';

export { Badge, badgeVariants };
